import type { ReactNode } from "react";

interface DisclosureProps {
  readonly title: string;
  /** One line shown while closed, so the key fact needs no tap. */
  readonly hint?: string;
  readonly children: ReactNode;
  /** Called each time the section is opened. */
  readonly onOpen?: () => void;
}

/**
 * A section that opens in place. Built on <details>, so it opens with a tap,
 * the keyboard and a screen reader with no script of its own.
 */
export function Disclosure({ title, hint, children, onOpen }: DisclosureProps) {
  return (
    <details className="group mt-6 border-y-[1.5px] border-ink" onToggle={(event) => event.currentTarget.open && onOpen?.()}>
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 py-3 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">
          <span className="label block text-ink">{title}</span>
          {hint && <span className="mt-1 block font-display text-[17px] leading-[1.35]">{hint}</span>}
        </span>
        <span aria-hidden="true" className="font-mono text-[20px] leading-none text-ink group-open:hidden">
          +
        </span>
        <span aria-hidden="true" className="hidden font-mono text-[20px] leading-none text-ink group-open:inline">
          −
        </span>
      </summary>
      <div className="pb-4">{children}</div>
    </details>
  );
}
